/**
 * Whether a client's messages actually leave.
 *
 * On by default. Off, the client keeps every screen — templates, the send
 * form, previews, contacts, history — and both its apps say up front that
 * sending is off; the server refuses each send before anything is recorded.
 * Made for a demo account that must be explorable without texting anybody,
 * such as the login a store's reviewers are given.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  ApiError,
  Badge,
  Card,
  CardBody,
  CardHeader,
  QueryPending,
  Switch,
  api,
  toast,
  useCan,
} from '@connect/web-shared';

import { messagingKeys } from './types';

interface SendingSettings {
  enabled: boolean;
}

export function SendingCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.channel.edit');
  const url = `/messaging/clients/${clientPublicId}/sending`;

  const settings = useQuery({
    queryKey: messagingKeys.sending(clientPublicId),
    queryFn: () => api.get<SendingSettings>(url),
  });

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.put<SendingSettings>(url, { enabled }),
    onSuccess: (result) => {
      queryClient.setQueryData(messagingKeys.sending(clientPublicId), result);
      toast.success(
        result.enabled ? 'Sending on' : 'Sending off',
        result.enabled
          ? `${clientName}'s messages go out again.`
          : `${clientName} keeps every screen, and nothing is sent.`,
      );
    },
    onError: (error) => toast.error('Could not change it', error instanceof ApiError ? error.message : undefined),
  });

  const enabled = settings.data?.enabled ?? true;

  return (
    <Card>
      <CardHeader
        title="Sending"
        actions={settings.data && !canEdit ? <Badge tone={enabled ? 'success' : 'warning'}>{enabled ? 'On' : 'Off'}</Badge> : null}
      />
      <CardBody className="space-y-3">
        {settings.data === undefined ? (
          <QueryPending query={settings} label="Loading" />
        ) : (
          <>
            {canEdit ? (
              <Switch
                label="Messages are sent"
                hint="Off, the client still sees every screen and can preview, but nothing is sent — for a demo or review account."
                checked={enabled}
                disabled={toggle.isPending}
                onChange={(event) => toggle.mutate(event.target.checked)}
              />
            ) : null}
            {enabled ? null : (
              <Alert tone="warning">
                Sending is off for {clientName}. Their apps say so on every send screen, and the server refuses each send,
                test and scheduled send.
              </Alert>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}
